<template>
	<div class="address-field d-flex flex-column ga-2" :class="{ 'field-error': !!errorMessage }" :data-field-id="field.id">
		<!-- Picked -->
		<div v-if="modelValue" class="address-card d-flex flex-column" data-test-id="address-picked">
			<img
				v-if="mapUrl"
				:src="mapUrl"
				class="address-map"
				alt=""
				data-test-id="address-map"
				loading="lazy"
				@error="mapFailed = true" />
			<div class="d-flex align-start ga-3 pa-4">
				<v-icon size="20" class="mt-1">icon-map-pin-stroke</v-icon>
				<div class="flex-grow-1 address-card-text">
					<div class="font-weight-medium">{{ pickedPrimaryLine }}</div>
					<div v-if="pickedSecondaryLine" class="address-card-secondary">{{ pickedSecondaryLine }}</div>
				</div>
				<button type="button" class="address-link" data-test-id="address-change" @click="clearAddress">Change</button>
			</div>
			<div class="px-4 pb-4">
				<v-text-field
					:model-value="pickedUnit"
					class="address-unit form-select"
					:rounded="false"
					placeholder="Apartment, suite, unit, gate code (optional)"
					autocomplete="address-line2"
					hide-details
					data-test-id="address-unit"
					@update:model-value="updateUnit"
					@blur="onUnitBlur" />
			</div>
		</div>

		<!-- Manual -->
		<div v-else-if="manualMode" class="address-card d-flex flex-column ga-3 pa-4" data-test-id="address-manual">
			<p v-if="!searchAvailable && !previewMode" class="address-notice mb-0" data-test-id="address-search-unavailable">
				Address search is unavailable right now. Enter the address below.
			</p>
			<p v-else-if="transientNotice" class="address-notice mb-0 d-flex align-center ga-2" data-test-id="address-search-transient">
				<span class="flex-grow-1">{{ TRANSIENT_NOTICE_TEXT }}</span>
				<button type="button" class="address-link" data-test-id="address-search-transient-dismiss" @click="transientNotice = false">
					Dismiss
				</button>
			</p>
			<v-text-field
				v-model="manual.street"
				class="form-select"
				:rounded="false"
				placeholder="Street address"
				autocomplete="address-line1"
				:error-messages="manualStreetError"
				data-test-id="address-manual-street"
				@update:model-value="manualStreetError = ''" />
			<v-text-field
				v-model="manual.unit"
				class="form-select"
				:rounded="false"
				placeholder="Apartment, suite, unit (optional)"
				autocomplete="address-line2"
				hide-details
				data-test-id="address-manual-unit" />
			<div class="d-flex ga-3 address-manual-row">
				<v-text-field
					v-model="manual.city"
					class="form-select flex-grow-1"
					:rounded="false"
					placeholder="City"
					autocomplete="address-level2"
					hide-details
					data-test-id="address-manual-city" />
				<v-text-field
					v-model="manual.state"
					class="form-select"
					:rounded="false"
					placeholder="State / Province"
					autocomplete="address-level1"
					hide-details
					data-test-id="address-manual-state" />
			</div>
			<div class="d-flex ga-3 address-manual-row">
				<v-text-field
					v-model="manual.postalCode"
					class="form-select"
					:rounded="false"
					placeholder="Postal code"
					autocomplete="postal-code"
					hide-details
					data-test-id="address-manual-postal" />
				<v-text-field
					v-model="manual.country"
					class="form-select flex-grow-1"
					:rounded="false"
					placeholder="Country"
					autocomplete="country-name"
					hide-details
					data-test-id="address-manual-country" />
			</div>
			<div class="d-flex align-center justify-space-between ga-3 flex-wrap">
				<button
					v-if="searchAvailable"
					type="button"
					class="address-link address-link-muted"
					data-test-id="address-manual-back"
					@click="backToSearch">
					Use address search instead
				</button>
				<span v-else></span>
				<v-btn color="primary" rounded :loading="confirmingManual" data-test-id="address-manual-use" @click="useManualAddress"
					>Use this address</v-btn
				>
			</div>
		</div>

		<!-- Searching -->
		<div v-else class="d-flex flex-column ga-1">
			<p v-if="transientNotice" class="address-notice mb-0 d-flex align-center ga-2" data-test-id="address-search-transient">
				<span class="flex-grow-1">{{ TRANSIENT_NOTICE_TEXT }}</span>
				<button type="button" class="address-link" data-test-id="address-search-transient-dismiss" @click="transientNotice = false">
					Dismiss
				</button>
			</p>
			<v-autocomplete
				ref="autocompleteRef"
				:model-value="null"
				class="form-select address-search"
				:rounded="false"
				:items="suggestions"
				:loading="searching"
				:search="searchTerm"
				item-title="text"
				item-value="placeId"
				return-object
				no-filter
				hide-details
				:hide-no-data="searchTerm.trim().length < MIN_SEARCH_LENGTH"
				:placeholder="field.placeholder || 'Search for an address'"
				:aria-label="field.name"
				:aria-required="required"
				:id="inputAttributes.id"
				:name="inputAttributes.name"
				:autocomplete="inputAttributes.autocomplete"
				:menu-props="suggestionMenuProps"
				data-test-id="address-search"
				@update:search="onSearchInput"
				@update:model-value="onSuggestionPicked"
				@blur="emit('blur')">
				<template #prepend-inner><v-icon size="18">icon-search-magnifying-glass</v-icon></template>
				<template v-if="geolocationAvailable" #append-inner>
					<v-btn
						icon
						variant="text"
						density="comfortable"
						aria-label="Use my current location"
						:loading="locating"
						data-test-id="address-use-location"
						@mousedown.prevent
						@click="useMyLocation">
						<v-icon size="18">mdi-crosshairs-gps</v-icon>
					</v-btn>
				</template>
				<template #item="{ item, props: itemProps }">
					<v-list-item v-bind="itemProps" :title="undefined" data-test-id="address-suggestion">
						<template #prepend><v-icon size="17">icon-map-pin-stroke</v-icon></template>
						<v-list-item-title class="font-weight-medium">{{ item.raw.mainText }}</v-list-item-title>
						<v-list-item-subtitle>{{ item.raw.secondaryText }}</v-list-item-subtitle>
						<template v-if="item.raw.distanceMeters != null" #append>
							<span class="address-distance">{{ formatDistance(item.raw.distanceMeters) }}</span>
						</template>
					</v-list-item>
				</template>
				<template #no-data>
					<v-list-item>
						<v-list-item-title class="text-medium-emphasis">
							{{ searching ? "Searching…" : "No addresses match that yet. Keep typing, or enter it manually below." }}
						</v-list-item-title>
					</v-list-item>
				</template>
			</v-autocomplete>
			<button type="button" class="address-link align-self-start" data-test-id="address-manual-toggle" @click="startManual">
				Can't find it? Enter the address manually
			</button>
		</div>

		<div v-if="errorMessage" class="field-error-message">{{ errorMessage }}</div>
	</div>
</template>

<script setup lang="ts">
	import { computed, reactive, ref, watch } from "vue";
	import type { AddressDetails } from "@liveswitch-contact/types";
	import { buildFormattedAddress } from "@liveswitch-contact/types";
	import api from "@/api";
	import { menuProps as globalMenuProps } from "@/plugins/vuetify";
	import { useSearchQuery } from "@/composables/useDebouncedSearch";
	import { useLatestRequest } from "@/composables/useLatestRequest";
	import { logWarn } from "@/helpers/logError";
	import type FormField from "@/types/forms/FormField";
	import type { AddressSuggestion, AddressValue } from "@/types/forms/AddressValue";
	import type { PublicFormInputAttributes } from "@/helpers/publicFormAutofill";

	const MIN_SEARCH_LENGTH = 3;
	const SEARCH_DEBOUNCE_MS = 250;
	const TRANSIENT_NOTICE_TEXT = "Address search is busy right now. Try again in a moment, or enter it manually below.";
	// The only failure that means "search is off for this form" — the 503 the proxy sends when no Google
	// key is configured. Everything else (our 429 limiter, a 502 upstream blip, a dropped connection) is
	// transient and must not cost the visitor the search box for the rest of the session.
	const ADDRESS_SEARCH_DISABLED_CODE = "ADDRESS_SEARCH_DISABLED";

	const props = defineProps<{
		field: Partial<FormField>;
		formId: string;
		searchEnabled: boolean;
		required: boolean;
		errorMessage?: string;
		inputAttributes: PublicFormInputAttributes;
		/** The organization's country (ISO 3166-1 alpha-2), used to rank suggestions near the business. */
		defaultCountryCode?: string | null;
		/**
		 * True inside the form builder's preview pane. The "search is unavailable" notice is written for a
		 * visitor filling in the live form; an admin editing the form reads it as their form being broken,
		 * so preview hides the notice and only shows the manual inputs (LSP-19563).
		 */
		previewMode?: boolean;
	}>();
	const modelValue = defineModel<AddressValue | null>({ default: null });
	const emit = defineEmits<{ blur: []; interaction: [] }>();

	const searchAvailable = ref(props.searchEnabled);
	const manualMode = ref(!props.searchEnabled);
	// True only while the visitor themselves asked for manual entry. `searchEnabled` arrives false and
	// flips true once the public form's definition fetch resolves, and in the builder's preview pane the
	// field mounts before that lands — so without this, manual mode set by the initial false would stick
	// and the search box would never appear (LSP-19563 local validation).
	const manualChosenByVisitor = ref(false);
	watch(
		() => props.searchEnabled,
		(enabled) => {
			searchAvailable.value = enabled;
			if (!enabled) {
				manualMode.value = true;
			} else if (!manualChosenByVisitor.value && !modelValue.value) {
				manualMode.value = false;
			}
		}
	);
	const searching = ref(false);
	const suggestions = ref<AddressSuggestion[]>([]);
	const locating = ref(false);
	const confirmingManual = ref(false);
	const manualStreetError = ref("");
	const mapFailed = ref(false);
	const transientNotice = ref(false);
	const autocompleteRef = ref<unknown>(null);
	const manual = reactive({ street: "", unit: "", city: "", state: "", postalCode: "", country: "" });
	let sessionToken: string | null = null;
	let userLocation: { latitude: number; longitude: number } | null = null;
	// Vuetify writes the chosen item's title back through `:search` as soon as a suggestion is
	// selected. Without this latch that writeback re-enters onSearchInput and schedules a fresh
	// debounced lookup — a second billed session for an address the user has already picked.
	let resolvingPick = false;
	// Every state-changing action (pick a suggestion, start manual entry, confirm a manual address,
	// Change/clear, use my location) claims the next operation id. An awaited completion only touches
	// state while it still owns the current operation, so place details for a suggestion the visitor
	// has already abandoned cannot overwrite the newer address they chose in the meantime (LSP-19563).
	let currentOperationId = 0;

	// Passing menu-props replaces Vuetify's global menu defaults, so spread them first (mirrors
	// WebFormV2's getSelectMenuProps) to keep the app's mobile viewport margin and max width.
	const suggestionMenuProps = { ...globalMenuProps, contentClass: "address-suggestion-menu" };

	// Reuse the app's debounce + latest-request composables rather than hand-rolling a timer and a
	// version counter: `setValueSilent` also cancels a pending debounce, which is what keeps a stale
	// keystroke from replaying a search after an address has already been picked.
	const {
		searchTerm,
		search: setSearchTerm,
		setValueSilent: setSearchTermSilent
	} = useSearchQuery({ debounceMs: SEARCH_DEBOUNCE_MS, searchCallback: onDebouncedSearch });
	const suggestionsRequest = useLatestRequest();

	const geolocationAvailable = computed(() => typeof navigator !== "undefined" && !!navigator.geolocation);
	const pickedUnit = computed(() => modelValue.value?.details?.unit ?? "");
	const mapUrl = computed(() => {
		const details = modelValue.value?.details;
		if (!details || details.latitude == null || details.longitude == null || mapFailed.value) return null;
		return api.publicFormAddressMapUrl(props.formId, details.latitude, details.longitude);
	});
	const pickedPrimaryLine = computed(() => {
		const details = modelValue.value?.details;
		if (!details) return "";
		const street = [details.streetNumber, details.street].filter(Boolean).join(" ");
		return street || details.formattedAddress || modelValue.value?.formatted || "";
	});
	const pickedSecondaryLine = computed(() => {
		const details = modelValue.value?.details;
		if (!details) return "";
		const region = [details.state, details.postalCode].filter(Boolean).join(" ");
		const rest = [details.city, region, details.country].filter(Boolean).join(", ");
		return rest && rest !== pickedPrimaryLine.value ? rest : "";
	});

	/**
	 * Claims the next operation id and clears the in-flight latches of the operation being superseded:
	 * a superseded completion returns without running its own cleanup, so the new operation owns them.
	 */
	function beginOperation(): number {
		currentOperationId += 1;
		resolvingPick = false;
		searching.value = false;
		locating.value = false;
		confirmingManual.value = false;
		return currentOperationId;
	}

	function ownsOperation(operationId: number): boolean {
		return operationId === currentOperationId;
	}

	/**
	 * A Places session token bundles the typing that leads to one pick into a single billed session.
	 * It is minted lazily on the first search and retired the moment the details call consumes it.
	 */
	function ensureSession(): string {
		if (!sessionToken) sessionToken = newSessionToken();
		return sessionToken;
	}

	function newSessionToken(): string {
		const cryptoApi = globalThis.crypto as Crypto | undefined;
		if (typeof cryptoApi?.randomUUID === "function") return cryptoApi.randomUUID();
		// The token only has to be unique per search session, not cryptographically strong, so a
		// random fallback keeps the field working in environments without crypto.randomUUID.
		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (character) => {
			const randomNibble = Math.floor(Math.random() * 16);
			const value = character === "x" ? randomNibble : (randomNibble & 0x3) | 0x8;
			return value.toString(16);
		});
	}

	/**
	 * `api._handleResponse` rejects with a PLAIN OBJECT, not an Error: the parsed JSON body with `code`
	 * (the body's own code, so our 503 arrives as "ADDRESS_SEARCH_DISABLED") plus `httpStatus`. A network
	 * failure rejects with a real Error instead and has neither, so it falls through as transient.
	 */
	function isSearchDisabledRejection(error: unknown): boolean {
		return !!error && typeof error === "object" && (error as { code?: unknown }).code === ADDRESS_SEARCH_DISABLED_CODE;
	}

	/** Latches the field to manual-only entry. Reserved for "search is off", never for a transient blip. */
	function fallbackToManual(reason: string, error?: unknown): void {
		logWarn("AddressField: address search unavailable, falling back to manual entry", {
			formId: props.formId,
			fieldId: props.field.id,
			reason,
			error: String(error ?? "")
		});
		searchAvailable.value = false;
		manualMode.value = true;
		transientNotice.value = false;
	}

	/** Keeps search available and surfaces a dismissable notice the next keystroke clears. */
	function noteTransientFailure(reason: string, error?: unknown): void {
		logWarn("AddressField: address search failed transiently, keeping search available", {
			formId: props.formId,
			fieldId: props.field.id,
			reason,
			error: String(error ?? "")
		});
		transientNotice.value = true;
	}

	/** The single decision point: only a configured-off 503 latches; everything else stays retryable. */
	function handleSearchFailure(reason: string, error: unknown): void {
		if (isSearchDisabledRejection(error)) fallbackToManual(reason, error);
		else noteTransientFailure(reason, error);
	}

	function onSearchInput(value: string | null): void {
		if (resolvingPick) return;
		// Typing supersedes an in-flight "use my location" lookup: without claiming the operation, a
		// slow reverse-geocode would still own the field and replace the visitor's suggestions and
		// query with the location results. Suggestions requests have their own supersession
		// (useLatestRequest), so only the location lookup needs the operation id bumped here.
		if (locating.value) beginOperation();
		transientNotice.value = false;
		emit("interaction");
		setSearchTerm(value ?? "");
	}

	function onDebouncedSearch(input: string): void {
		if (input.length < MIN_SEARCH_LENGTH) {
			suggestions.value = [];
			return;
		}
		void fetchSuggestions(input);
	}

	async function fetchSuggestions(input: string): Promise<void> {
		const request = suggestionsRequest.startRequest();
		searching.value = true;
		try {
			const response = await api.getPublicFormAddressSuggestions(props.formId, {
				input,
				session: ensureSession(),
				latitude: userLocation?.latitude,
				longitude: userLocation?.longitude,
				region: preferredRegion()
			});
			if (suggestionsRequest.isSuperseded(request.requestId)) return;
			suggestions.value = Array.isArray(response?.suggestions) ? response.suggestions : [];
		} catch (error) {
			if (suggestionsRequest.isSuperseded(request.requestId)) return;
			handleSearchFailure("suggestions_request_failed", error);
		} finally {
			if (!suggestionsRequest.isSuperseded(request.requestId)) searching.value = false;
		}
	}

	/**
	 * Region for ranking suggestions. The organization's own country comes first: a visitor filling a
	 * business's form is almost always looking for an address in that business's country, and the browser
	 * locale is a poor stand-in — many report a bare "en", which ranked a Surrey BC address below Phoenix
	 * and rural France. Falls back to the locale's region, then to nothing (Google ranks unbiased).
	 */
	function preferredRegion(): string | undefined {
		const organizationCountry = (props.defaultCountryCode || "").trim();
		if (/^[A-Za-z]{2}$/.test(organizationCountry)) return organizationCountry.toUpperCase();
		const locale = typeof navigator !== "undefined" ? navigator.language : "";
		const region = (locale || "").split("-")[1];
		return region && /^[A-Za-z]{2}$/.test(region) ? region.toUpperCase() : undefined;
	}

	function applyDetails(details: AddressDetails): void {
		mapFailed.value = false;
		modelValue.value = { formatted: buildFormattedAddress(details), details };
		emit("interaction");
	}

	async function onSuggestionPicked(suggestion: AddressSuggestion | null): Promise<void> {
		if (!suggestion) return;
		// Retire the search before awaiting details: latch out the title writeback, cancel any pending
		// debounce, and supersede the in-flight suggestions request so a late rejection from a query
		// the user has already moved past cannot disable search for the rest of the session.
		const operationId = beginOperation();
		resolvingPick = true;
		setSearchTermSilent("");
		suggestions.value = [];
		suggestionsRequest.startRequest();
		const session = ensureSession();
		sessionToken = null; // details completes the billed session
		searching.value = true;
		try {
			const response = await api.getPublicFormAddressPlace(props.formId, suggestion.placeId, session);
			if (!ownsOperation(operationId)) return;
			applyDetails({ ...response.details, unit: null });
		} catch (error) {
			logWarn("AddressField: place details failed, keeping the suggestion text", {
				formId: props.formId,
				placeId: suggestion.placeId,
				error: String(error)
			});
			if (!ownsOperation(operationId)) return;
			applyDetails({
				source: "search",
				placeId: suggestion.placeId,
				formattedAddress: suggestion.text,
				latitude: null,
				longitude: null,
				streetNumber: null,
				street: null,
				unit: null,
				city: null,
				state: null,
				postalCode: null,
				country: null
			});
		} finally {
			if (ownsOperation(operationId)) {
				searching.value = false;
				setSearchTermSilent("");
				suggestions.value = [];
				resolvingPick = false;
			}
		}
	}

	function useMyLocation(): void {
		if (!geolocationAvailable.value) return;
		const operationId = beginOperation();
		// Also retire any in-flight typing lookup. beginOperation only guards the location path's own
		// awaits; fetchSuggestions checks the request scope instead, so without this a slow "12 Main"
		// response could land after the location results and replace them with suggestions for the query
		// the visitor abandoned — leaving a list that does not match the place they asked for.
		suggestionsRequest.startRequest();
		locating.value = true;
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				userLocation = { latitude: position.coords.latitude, longitude: position.coords.longitude };
				try {
					const response = await api.reverseGeocodePublicFormAddress(props.formId, userLocation.latitude, userLocation.longitude);
					if (!ownsOperation(operationId)) return;
					const results: AddressDetails[] = Array.isArray(response?.results) ? response.results : [];
					suggestions.value = results
						.filter((result) => result.placeId)
						.map((result) => ({
							placeId: result.placeId as string,
							text: result.formattedAddress,
							mainText: [result.streetNumber, result.street].filter(Boolean).join(" ") || result.formattedAddress,
							secondaryText: [result.city, result.state, result.country].filter(Boolean).join(", "),
							distanceMeters: null
						}));
					setSearchTermSilent(" ");
					(autocompleteRef.value as { focus?: () => void } | null)?.focus?.();
				} catch (error) {
					// Never latch from the geolocation path: "use my location" failing says nothing about
					// whether typing an address still works, and taking the search box away for it is worse
					// than the failed convenience it replaces.
					if (!ownsOperation(operationId)) return;
					noteTransientFailure("reverse_geocode_failed", error);
				} finally {
					if (ownsOperation(operationId)) locating.value = false;
				}
			},
			(error) => {
				if (!ownsOperation(operationId)) return;
				locating.value = false;
				noteTransientFailure("geolocation_unavailable", `code ${error.code}`);
			},
			{ timeout: 10_000 }
		);
	}

	/** Leaving manual entry is a state-changing action too: an in-flight manual geocode must not land. */
	function backToSearch(): void {
		beginOperation();
		manualChosenByVisitor.value = false;
		manualMode.value = false;
	}

	function startManual(): void {
		beginOperation();
		manualChosenByVisitor.value = true;
		manualMode.value = true;
		emit("interaction");
	}

	async function useManualAddress(): Promise<void> {
		// `v-model.trim` is inert on Vuetify fields (VTextField assigns the raw value), so every part is
		// trimmed here instead — otherwise a whitespace-only street passes the blank guard below and is
		// sent to geocode and saved as the formatted address.
		const street = manual.street.trim();
		// The button stays enabled and simply no-ops with an inline error until a street is entered.
		if (!street) {
			manualStreetError.value = "Street address is required.";
			return;
		}
		const operationId = beginOperation();
		confirmingManual.value = true;
		const parts: AddressDetails = {
			source: "manual",
			placeId: null,
			formattedAddress: "",
			latitude: null,
			longitude: null,
			streetNumber: null,
			street,
			unit: manual.unit.trim() || null,
			city: manual.city.trim() || null,
			state: manual.state.trim() || null,
			postalCode: manual.postalCode.trim() || null,
			country: manual.country.trim() || null
		};
		const line = buildFormattedAddress({ ...parts, unit: null });
		parts.formattedAddress = line;
		if (searchAvailable.value) {
			try {
				const response = await api.geocodePublicFormAddress(props.formId, line);
				if (response?.location) {
					parts.latitude = response.location.latitude;
					parts.longitude = response.location.longitude;
				}
			} catch (error) {
				logWarn("AddressField: manual geocode failed, saving without coordinates", { formId: props.formId, error: String(error) });
			}
			// The visitor may have pressed Change or gone back to search while the geocode was in
			// flight; applying this address now would resurrect the one they just abandoned.
			if (!ownsOperation(operationId)) return;
		}
		confirmingManual.value = false;
		manualMode.value = false;
		applyDetails(parts);
		// The manual card has no single input the user leaves, so completing it is the blur a parent
		// that validates on blur needs to see.
		emit("blur");
	}

	// The unit input is controlled by `pickedUnit`, so the raw value is kept in the details while the
	// user types — writing a trimmed value back mid-typing would swallow the space they just pressed.
	// `buildFormattedAddress` trims the unit itself, so the saved one-line value is never affected.
	function updateUnit(unit: string | null): void {
		if (!modelValue.value) return;
		const details = { ...modelValue.value.details, unit: unit || null };
		modelValue.value = { formatted: buildFormattedAddress(details), details };
		emit("interaction");
	}

	function onUnitBlur(): void {
		const currentUnit = modelValue.value?.details.unit;
		if (modelValue.value && typeof currentUnit === "string" && currentUnit !== currentUnit.trim()) {
			const details = { ...modelValue.value.details, unit: currentUnit.trim() || null };
			modelValue.value = { formatted: buildFormattedAddress(details), details };
		}
		emit("blur");
	}

	function clearAddress(): void {
		beginOperation();
		modelValue.value = null;
		manualChosenByVisitor.value = false;
		manualMode.value = !searchAvailable.value;
		sessionToken = null;
		setSearchTermSilent("");
		suggestions.value = [];
		emit("interaction");
	}

	function formatDistance(meters: number): string {
		return meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`;
	}
</script>

<style lang="scss" scoped>
	.address-card {
		border: var(--form-border-width, 1px) solid var(--form-field-border-color, #e0e0e0);
		border-radius: var(--form-border-radius, 8px);
		background-color: var(--form-field-background, #ffffff);
		overflow: hidden;
	}

	.address-field.field-error .address-card,
	.address-field.field-error :deep(.address-search .v-field__outline) {
		border-color: var(--form-error-text-color, #f44336);
		color: var(--form-error-text-color, #f44336);
	}

	.address-map {
		display: block;
		width: 100%;
		height: 150px;
		object-fit: cover;
	}

	.address-card-text {
		min-width: 0;
		overflow-wrap: anywhere;
	}

	.address-card-secondary,
	.address-notice {
		font-size: 0.875em;
		opacity: 0.75;
	}

	.address-link {
		border: none;
		background: transparent;
		padding: 0;
		color: var(--form-primary-color, #1976d2);
		font: inherit;
		font-size: 0.875em;
		font-weight: 600;
		cursor: pointer;
	}

	.address-link-muted {
		color: inherit;
		opacity: 0.7;
	}

	.address-distance {
		font-size: 0.75em;
		font-weight: 600;
		padding: 2px 8px;
		border-radius: 9999px;
		background: rgba(0, 112, 255, 0.12);
	}

	.address-manual-row > * {
		min-width: 0;
	}

	/* The suggestion menu is teleported outside this component, so it needs global styling. Long
	 * addresses must wrap rather than widen the menu (mirrors WebFormV2's .form-select-menu rule). */
	:global(.address-suggestion-menu .v-list-item-title) {
		overflow-wrap: anywhere;
		white-space: normal;
	}

	@media (max-width: 480px) {
		.address-manual-row {
			flex-direction: column;
		}
	}

	.field-error-message {
		color: var(--form-error-text-color, #f44336);
		font-size: 0.75rem;
	}

	/* Copied from WebFormV2.vue's scoped styles: `.form-select` is defined there and a parent's
	 * scoped attribute never reaches a child component's own elements, so the Address field's
	 * inputs need the same rules locally to match the neighbouring public-form fields. */
	.form-select {
		width: 100%;
		cursor: pointer;
		font-family: inherit;

		:deep(.v-field) {
			--v-field-padding-start: var(--form-field-padding-inline);
			--v-field-padding-end: var(--form-field-padding-inline);
			--v-field-padding-top: var(--form-field-padding-block);
			--v-field-padding-bottom: var(--form-field-padding-block);
			--v-input-padding-top: 0px;
			background-color: var(--form-field-background, #ffffff);
			border-radius: var(--form-border-radius, 8px);
			color: inherit;
			font-family: inherit;
			font-size: inherit;
		}

		/* Vuetify's outlined VField has a 56px minimum and a fixed 1.5rem fallback.
		 * Replace both with the native field's line box, padding, and two border widths
		 * so every form style preset renders select controls at the same size as inputs. */
		:deep(.v-field__input) {
			min-height: calc(
				1.2em + var(--form-field-padding-block) + var(--form-field-padding-block) + var(--form-border-width) +
					var(--form-border-width)
			);
			min-height: calc(
				1lh + var(--form-field-padding-block) + var(--form-field-padding-block) + var(--form-border-width) +
					var(--form-border-width)
			);
		}

		:deep(.v-field__outline) {
			--v-field-border-opacity: 1;
			--v-field-border-width: var(--form-border-width);
			color: var(--form-field-border-color, #e0e0e0);
		}

		:deep(.v-field__input) {
			color: inherit;
			font-family: inherit;
			font-size: inherit;
		}

		:deep(.v-field__input::placeholder) {
			color: var(--form-placeholder-color, #9e9e9e);
			opacity: 1;
		}

		&:deep(.v-field--focused .v-field__outline) {
			--v-field-border-opacity: 1;
			color: var(--form-primary-color, #1976d2);
		}

		&.field-error :deep(.v-field__outline) {
			--v-field-border-opacity: 1;
			color: var(--form-error-text-color, #f44336);
		}
	}
</style>
